/**
 * Users API — profiles and business claiming via Supabase.
 */

import supabase from "../config/supabase";
import { phoneKey } from "../utils/phone";

/** Public card on the users table. Phone and email are not in this list. */
export const USER_CARD_EMBED =
  "id, first_name, last_name, profile_image_url, location, role, is_test";

export function isMissingRelation(error) {
  const code = error?.code || "";
  const msg = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();
  return (
    code === "PGRST205" ||
    code === "42P01" ||
    (msg.includes("user_profiles") &&
      (msg.includes("could not find") ||
        msg.includes("does not exist") ||
        msg.includes("schema cache")))
  );
}

function groupBy(rows, key) {
  const map = new Map();
  for (const row of rows || []) {
    const list = map.get(row[key]) || [];
    list.push(row);
    map.set(row[key], list);
  }
  return map;
}

async function attachBusinessesAndSubscriptions(profile) {
  if (!profile?.id) return profile;
  const [businesses, subscriptions] = await Promise.all([
    supabase.from("businesses").select("*").eq("user_id", profile.id),
    supabase.from("subscriptions").select("*").eq("user_id", profile.id),
  ]);
  return {
    ...profile,
    businesses: businesses.data || [],
    subscriptions: subscriptions.data || [],
  };
}

async function hydrateProfiles(rows) {
  const list = rows || [];
  const ids = list.map((row) => row.id).filter(Boolean);
  if (!ids.length) return list;
  const [businesses, subscriptions] = await Promise.all([
    supabase.from("businesses").select("*").in("user_id", ids),
    supabase.from("subscriptions").select("*").in("user_id", ids),
  ]);
  const bizBy = groupBy(businesses.data, "user_id");
  const subBy = groupBy(subscriptions.data, "user_id");
  return list.map((row) => ({
    ...row,
    businesses: bizBy.get(row.id) || [],
    subscriptions: subBy.get(row.id) || [],
  }));
}

export async function fetchMemberProfile(userId) {
  if (!userId) return { success: false, error: "Missing user" };

  const fromView = await supabase
    .from("user_profiles")
    .select("*")
    .eq("id", userId)
    .maybeSingle();

  if (!fromView.error) {
    if (!fromView.data) return { success: false, error: "Not found" };
    return {
      success: true,
      data: await attachBusinessesAndSubscriptions(fromView.data),
    };
  }

  if (!isMissingRelation(fromView.error)) {
    return { success: false, error: fromView.error.message };
  }

  // View not deployed yet — this is what keeps the app working between
  // shipping and applying 033. After 033 the path is dead.
  const { data, error } = await supabase
    .from("users")
    .select("*, businesses(*), subscriptions(*)")
    .eq("id", userId)
    .single();

  if (error) return { success: false, error: error.message };
  return { success: true, data };
}

export async function listMemberProfiles({ role, limit } = {}) {
  let query = supabase
    .from("user_profiles")
    .select("*")
    .order("created_at", { ascending: false });
  if (role) query = query.eq("role", role);
  if (limit) query = query.limit(limit);

  const fromView = await query;
  if (!fromView.error) {
    return { success: true, data: await hydrateProfiles(fromView.data || []) };
  }

  if (!isMissingRelation(fromView.error)) {
    return { success: false, error: fromView.error.message };
  }

  let fallback = supabase
    .from("users")
    .select("*, businesses(*), subscriptions(*)")
    .order("created_at", { ascending: false });
  if (role) fallback = fallback.eq("role", role);
  if (limit) fallback = fallback.limit(limit);
  const { data, error } = await fallback;
  if (error) return { success: false, error: error.message };
  return { success: true, data: data || [] };
}

const usersApi = {
  /**
   * Look up a single member by mobile number.
   *
   * Goes through the find_member_by_phone RPC rather than querying users
   * directly, so the columns that come back are fixed server-side and the
   * stored-format mismatch is reconciled in one place. Returns
   * `{ success: true, data: null }` for "nobody has that number" — a miss is a
   * normal answer here, not a failure.
   */
  async findByPhone(phone) {
    if (!phoneKey(phone)) {
      return { success: false, error: "Enter a full mobile number." };
    }

    const { data, error } = await supabase.rpc("find_member_by_phone", {
      p_phone: phoneKey(phone),
    });

    if (error) return { success: false, error: error.message };

    const row = Array.isArray(data) ? data[0] : data;
    if (!row) return { success: true, data: null };

    return {
      success: true,
      data: {
        id: row.id,
        name: `${row.first_name || ""} ${row.last_name || ""}`.trim(),
        location: row.location || "",
        avatar: row.profile_image_url || null,
        isSelf: Boolean(row.is_self),
        alreadySaved: Boolean(row.already_saved),
      },
    };
  },

  async getUser(userId) {
    return fetchMemberProfile(userId);
  },

  async updateUser(userId, body) {
    const { error } = await supabase.from("users").update(body).eq("id", userId);

    if (error) return { success: false, error: error.message };
    return fetchMemberProfile(userId);
  },

  async claimBusiness() {
    return {
      success: false,
      error: "Direct business claims are disabled. Submit a claim request for admin review.",
    };
  },

  /**
   * Submit a request to claim ownership of a (scraped/unclaimed) service listing.
   * Verification is admin-reviewed for launch: the request becomes a pending
   * item in moderation_queue, which the admin approves/declines on the
   * moderation screen. On approval, admin-actions transfers ownership.
   */
  async requestClaim(serviceId, { note } = {}) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    // Guard against duplicate pending claims for the same listing.
    const { data: existing } = await supabase
      .from("moderation_queue")
      .select("id")
      .eq("entity_type", "business_claim")
      .eq("entity_id", serviceId)
      .eq("status", "pending")
      .limit(1);
    if (existing && existing.length) {
      return { success: false, error: "A claim for this business is already awaiting review." };
    }

    // Snapshot the listing so the admin can review without extra lookups.
    const { data: svc } = await supabase
      .from("services")
      .select("title, business_address, mobile, abn")
      .eq("id", serviceId)
      .single();

    const { error } = await supabase.from("moderation_queue").insert({
      entity_type: "business_claim",
      entity_id: serviceId,
      submitted_by: user.id,
      status: "pending",
      content_snapshot: {
        title: svc?.title || "Business listing",
        business_address: svc?.business_address || "",
        mobile: svc?.mobile || "",
        abn: svc?.abn || "",
        note: note || "",
      },
    });

    if (error) return { success: false, error: error.message };
    return { success: true };
  },
};

export default usersApi;
